/** XeAgents.ai **/
import { useEffect, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Building2, CheckCircle, Loader2, AlertCircle, Mail } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { AuthStorage } from "@/lib/auth-storage";

interface InvitationDetails {
  id: string;
  email: string;
  role: string;
  organization: {
    name: string;
    country: string;
  };
  invitedBy: {
    name: string;
  };
  expiresAt: string;
  status: string;
}

export default function AcceptInvitation() {
  const [, params] = useRoute("/invitation/:token");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const token = params?.token;

  const [isAuthenticated] = useState(() => AuthStorage.isAuthenticated());

  // Fetch invitation details
  const { data: invitation, isLoading, error } = useQuery<InvitationDetails>({
    queryKey: [`/api/organizations/invitations/${token}`],
    enabled: !!token,
    retry: false,
  });

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/organizations/invitations/${token}/accept`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to accept invitation");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Invitation accepted!",
        description: "Welcome to the organization",
      });
      // Redirect to organization overview
      setTimeout(() => {
        setLocation("/app/organization");
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to accept invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAccept = () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      setLocation(`/signin?redirect=/invitation/${token}`);
      return;
    }
    acceptMutation.mutate();
  };

  const isExpired = invitation && new Date(invitation.expiresAt) < new Date();

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground">
              This invitation link is invalid or malformed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
            <h2 className="text-xl font-semibold mb-2">Loading invitation...</h2>
            <p className="text-muted-foreground">Please wait while we fetch the details.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Invitation Not Found</h2>
            <p className="text-muted-foreground mb-4">
              This invitation link is invalid, has expired, or has already been used.
            </p>
            <Button onClick={() => setLocation("/signin")}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold mb-2">Invitation Expired</h2>
            <p className="text-muted-foreground mb-4">
              This invitation has expired. Please contact the organization owner to send a new invitation.
            </p>
            <div className="p-4 rounded-lg bg-muted text-left mb-4">
              <p className="text-sm">
                <strong>Organization:</strong> {invitation.organization.name}
              </p>
              <p className="text-sm mt-1">
                <strong>Invited by:</strong> {invitation.invitedBy.name}
              </p>
            </div>
            <Button onClick={() => setLocation("/signin")}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.status !== "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-semibold mb-2">Already Accepted</h2>
            <p className="text-muted-foreground mb-4">
              This invitation has already been accepted.
            </p>
            <Button onClick={() => setLocation("/app/organization")}>
              Go to Organization
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-background">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">You're Invited!</CardTitle>
          <CardDescription>
            You've been invited to join an organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Organization Details */}
          <div className="p-4 rounded-lg border bg-muted/50 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{invitation.organization.name}</p>
                <p className="text-sm text-muted-foreground">
                  {invitation.organization.country}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Your role:</span>
              <Badge variant={invitation.role === "owner" ? "default" : "secondary"}>
                {invitation.role}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Invited by:</span>
              <span className="font-medium">{invitation.invitedBy.name}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {!isAuthenticated && (
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-center">
                You need to sign in or create an account to accept this invitation
              </div>
            )}
            <Button
              onClick={handleAccept}
              disabled={acceptMutation.isPending}
              className="w-full"
              size="lg"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : isAuthenticated ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Accept Invitation
                </>
              ) : (
                "Sign In to Accept"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setLocation("/")}
              className="w-full"
            >
              Maybe Later
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-muted-foreground text-center">
            By accepting this invitation, you'll join {invitation.organization.name} as a{" "}
            {invitation.role} and gain access to their resources.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}